import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import TopBar from "@/components/TopBar";
import FestivePattern from "@/components/FestivePattern";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function CodeEntry() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const normalized = code.toUpperCase().trim();
    if (normalized.length < 4) {
      setError(t("code.error"));
      return;
    }
    setLoading(true);
    try {
      await api.get(`/public/events/${normalized}`);
      navigate(`/event/${normalized}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || t("code.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] relative overflow-hidden">
      <div className="absolute inset-0">
        <FestivePattern />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-[#FFF9FB]" />

      <div className="relative z-10">
        <TopBar hideBrand />

        <div className="max-w-md mx-auto px-5 pb-24">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            className="gap-2 -ml-3 mt-0 text-[#5C4664] hover:bg-[#FCE7F3]"
          >
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </Button>

          <div className="mt-6 flex justify-center">
            <span
              className="milk-shimmer-logo"
              style={{ "--shimmer-mask": "url(/milkado-logo.png)" }}
            >
              <img
                src="/milkado-logo.png"
                alt="MILKADO"
                data-testid="code-logo"
                className="block w-[160px] sm:w-[220px] h-auto drop-shadow-[0_6px_16px_rgba(216,27,140,0.18)]"
              />
            </span>
          </div>

          <div className="mt-6 text-center">
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] leading-tight tracking-tight">
              {t("code.title")}
            </h1>
            <p className="mt-3 text-[#5C4664]">{t("code.sub")}</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-7">
            <input
              data-testid="code-input"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))
              }
              placeholder={t("code.placeholder")}
              maxLength={6}
              autoFocus
              inputMode="text"
              autoCapitalize="characters"
              className="w-full text-center font-display text-[2.1rem] sm:text-[2.6rem] font-black tracking-[0.35em] uppercase border-b-2 border-[#2A0E3C] bg-transparent py-3 focus:border-[#D81B8C] focus:outline-none placeholder:text-[#F2E2EC]"
            />
            {error && (
              <p data-testid="code-error" className="mt-4 text-sm text-[#D81B8C] font-medium text-center">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="code-submit"
              className="milk-shimmer milk-glow-pink mt-7 w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold text-base gap-2 active:scale-95 transition-transform"
            >
              {loading ? t("common.loading") : t("code.submit")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <div className="mt-14 pt-8 border-t border-[#F2E2EC] text-center">
            <p className="text-sm text-[#5C4664]">{t("code.noCode")}</p>
            <a
              href={`https://wa.me/243846103822?text=${encodeURIComponent(t("code.requestMessage"))}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="request-code-btn"
              className="milk-shimmer milk-glow-green mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white px-6 h-11 font-semibold text-sm transition-colors"
            >
              <svg viewBox="0 0 32 32" className="w-4 h-4 fill-current" aria-hidden="true">
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.317.205-.66.205-1.005 0-.058 0-.115-.014-.158-.044-.144-2.378-1.276-2.638-1.276zm-2.967 7.83c-1.563 0-3.083-.43-4.405-1.234l-3.165 1.005 1.03-3.05a8.385 8.385 0 0 1-1.633-4.97c0-4.654 3.794-8.435 8.452-8.435a8.31 8.31 0 0 1 5.97 2.484 8.32 8.32 0 0 1 2.473 5.967c-.014 4.655-3.808 8.235-8.722 8.235zm0-18.853c-5.749 0-10.42 4.67-10.42 10.405 0 1.834.47 3.55 1.347 5.04l-1.5 4.36 4.534-1.434c1.45.78 3.04 1.117 4.67 1.117 5.748 0 10.42-4.67 10.42-10.405-.115-5.738-4.788-10.408-10.421-10.408z"/>
              </svg>
              {t("code.requestCode")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
